
import {defineComponent, PropType} from "vue";
import {ContentPageRestDto, WysiwygContentPageRestDto} from "@/api/pharma-cms-content-page/models";
import {Exception} from "@/api/interfaces";
import AutoSaveFeedback from "@/components/UI/AutoSaveFeedback.vue";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import Subtitle from "@/components/UI/Subtitle.vue";
import ability from "@/config/ability";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import {ContentPageRestService} from "@/services/rest/cms-content-page/ContentPageRestService";

export default defineComponent({
  name: "ContentPagePageSettings",
  emits: ["saved"],
  components: {BaseOffcanvasButtonClose, Subtitle, AutoSaveFeedback},
  props: {
    pageType: {
      type: String,
      required: false
    },
    /**
     * The page being edited. Handed down instead of read from a store, so this offcanvas and the
     * canvas behind it can never disagree about which page they show.
     */
    contentPage: {
      type: Object as PropType<WysiwygContentPageRestDto>,
      required: true,
    },
  },
  data() {
    return {
      exceptions: [] as Exception[],
      autoSaveFeedbackStatus: AutoSaveFeedbackStatusEnum.NONE as AutoSaveFeedbackStatusEnum,

      // The form edits its own copy rather than writing into the prop; the page picks the saved
      // values back up through the reload it does on the 'saved' event.
      cp: {} as ContentPageRestDto,
    };
  },
  watch: {
    contentPage: {
      handler() {
        this.resetFormData();
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    isMultiLingual(): boolean {
      return ability.can('use-feature', 'MULTILINGUAL');
    },
  },
  methods: {
    /**
     * Called by the page every time this offcanvas is opened. Reopening it is how the user cancels:
     * the form is rebuilt from the page as it was last loaded, so values that could not be saved are
     * not carried into the new session. The contentPage watcher cannot do this, as the page itself
     * did not change.
     */
    reset(): void {
      this.exceptions = [];
      this.resetFormData();
    },
    resetFormData(): void {
      this.cp = {...(this.contentPage.cp ?? {})} as ContentPageRestDto;
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.NONE;
    },
    async save(): Promise<void> {
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.BUSY;
      this.exceptions = [];

      try {
        await ContentPageRestService.getInstance()
          .updateContentPage(this.cp as ContentPageRestDto, this.contentPage.id);

        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.SUCCESS;
        this.$emit('saved');
      } catch (exceptions: any) {
        this.exceptions = exceptions;

        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.FAILED;
      }
    },
    onChange(): void {
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES;
    },
  },
});
